import React, { useState } from 'react';
import { 
  Paper, 
  TextField, 
  Button, 
  Box, 
  Chip,
  InputAdornment,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const UNIVERSITIES = [
  { 
    name: 'MUST (Meru University)', 
    value: 'must',
    url: 'https://exampapers.must.ac.ke',
    schools: [
      { name: 'School of Computing and Informatics (SCI)', value: 'sci' },
      { name: 'School of Agriculture and Food Sciences (SAFS)', value: 'safs' },
      { name: 'School of Business and Economics (SBE)', value: 'sbe' },
      { name: 'School of Education (SEA)', value: 'sea' },
      { name: 'School of Pure and Applied Sciences (SPAS)', value: 'spas' },
      { name: 'School of Nursing (SON)', value: 'son' },
      { name: 'School of Public Health (SPH)', value: 'sph' },
    ]
  },
];

function SearchBar({ onSearch, loading }) {
  const [selectedUniversity, setSelectedUniversity] = useState('must');
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [keywords, setKeywords] = useState('');

  const currentUniversity = UNIVERSITIES.find(u => u.value === selectedUniversity);
  const currentYear = new Date().getFullYear();
  const yearRange = currentYear - 2015 + 1; // From 2015 to current year
  const years = ['all', ...Array.from({ length: yearRange }, (_, i) => currentYear - i)];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let searchUrl = currentUniversity.url;
    let searchKeywords = keywords;
    
    onSearch(searchUrl, searchKeywords, selectedSchool, selectedYear);
  };

  const quickFilters = ['exam', 'Mathematics', 'supplementary'];

  const handleQuickFilter = (filter) => {
    setKeywords(prev => prev ? `${prev} ${filter}` : filter);
  };

  return (
    <Paper className="glass p-6 rounded-2xl">
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* University, School, and Year Selection */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>University</InputLabel>
              <Select
                value={selectedUniversity}
                label="University"
                onChange={(e) => {
                  setSelectedUniversity(e.target.value);
                  setSelectedSchool('');
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SchoolIcon sx={{ color: '#009688', ml: 1 }} />
                  </InputAdornment>
                }
              >
                {UNIVERSITIES.map((uni) => (
                  <MenuItem key={uni.value} value={uni.value}>
                    {uni.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>School (Optional)</InputLabel>
              <Select
                value={selectedSchool}
                label="School (Optional)"
                onChange={(e) => setSelectedSchool(e.target.value)}
                disabled={!currentUniversity}
              >
                <MenuItem value="">
                  <em>All Schools</em>
                </MenuItem>
                {currentUniversity?.schools.map((school) => (
                  <MenuItem key={school.value} value={school.value}>
                    {school.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ color: '#009688', ml: 1 }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">
                  <em>All Years</em>
                </MenuItem>
                {years.slice(1).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Portal URL Display */}
          <Box sx={{ 
            p: 2, 
            bgcolor: 'rgba(0, 150, 136, 0.1)', 
            borderRadius: 1,
            border: '1px solid rgba(0, 150, 136, 0.2)'
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Portal URL:
            </Typography>
            <Typography variant="body2" sx={{ color: '#009688', fontFamily: 'monospace' }}>
              {currentUniversity?.url || 'Select a university'}
            </Typography>
          </Box>

          {/* Keywords */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Unit code (e.g., CIT3350) or keywords..."
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#009688' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'rgba(0, 150, 136, 0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(0, 150, 136, 0.5)',
                },
              },
            }}
          />

          {/* Quick Filters and Search Button */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {quickFilters.map((filter) => (
                <Chip 
                  key={filter}
                  label={filter} 
                  size="small" 
                  onClick={() => handleQuickFilter(filter)}
                  sx={{ 
                    bgcolor: 'rgba(0, 150, 136, 0.2)',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 150, 136, 0.3)',
                    }
                  }} 
                />
              ))}
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || !selectedUniversity}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{
                bgcolor: '#009688',
                '&:hover': {
                  bgcolor: '#00796b',
                },
                px: 4,
              }}
            >
              {loading ? 'Scanning...' : 'Discover Papers'}
            </Button>
          </Box>
        </Box>
      </form>
    </Paper>
  );
}

export default SearchBar;
